
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Languages, Loader2, AlertCircle } from "lucide-react";
import { useTranslateCourseContent } from "@/hooks/useTranslateCourseContent";
import { useTranslationProgress } from "@/hooks/useTranslationProgress";
import { TranslationProgressIndicator } from "./TranslationProgressIndicator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useQueryClient } from "@tanstack/react-query";

interface TranslateCourseContentDialogProps {
  courseId?: string;
  contentType?: 'modules' | 'questions' | 'both';
}

export function TranslateCourseContentDialog({ 
  courseId, 
  contentType = 'both' 
}: TranslateCourseContentDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();
  
  // Only fetch progress for questions (not modules)
  // Enable polling when dialog is open to get live updates during translation
  const { data: progress, isLoading: isLoadingProgress, refetch } = useTranslationProgress(
    contentType === 'questions' || contentType === 'both' ? courseId : undefined,
    25, // batchSize
    isOpen // Enable polling when dialog is open
  );
  
  const translateMutation = useTranslateCourseContent(
    courseId, 
    contentType
  );

  // Force refetch when dialog opens (ignore stale time)
  useEffect(() => {
    if (isOpen && (contentType === 'questions' || contentType === 'both')) {
      refetch({ cancelRefetch: true });
    }
  }, [isOpen, contentType, refetch]);

  // Refetch progress after successful translation
  useEffect(() => {
    if (translateMutation.isSuccess) {
      refetch({ cancelRefetch: true });
      queryClient.invalidateQueries({ queryKey: ['translation-progress'] });
    }
  }, [translateMutation.isSuccess, refetch, queryClient]);

  // Invalidate query when dialog closes after translation
  useEffect(() => {
    if (!isOpen && translateMutation.isSuccess) {
      queryClient.invalidateQueries({ queryKey: ['translation-progress'] });
      translateMutation.reset();
    }
  }, [isOpen, translateMutation, queryClient]);

  const handleTranslate = async () => {
    try {
      await translateMutation.mutateAsync();
      setIsOpen(false);
    } catch (error) {
      // Error is already handled in the mutation
      console.error('Translation failed:', error);
    }
  };

  const getButtonText = () => {
    if (contentType === 'questions' && progress) {
      if (progress.remainingCount === 0 && progress.spanishCount > 0) {
        return 'Re-translate Questions';
      } else if (progress.shouldResume) {
        return `Resume Translation (${progress.remainingCount} remaining)`;
      }
    }
    
    switch (contentType) {
      case 'modules':
        return 'Translate Modules to Spanish';
      case 'questions':
        return 'Translate Questions to Spanish';
      default:
        return 'Translate to Spanish';
    }
  };

  const getButtonVariant = () => {
    if (contentType === 'questions' && progress?.remainingCount === 0 && progress?.spanishCount > 0) {
      return 'outline';
    }
    return 'pill';
  };

  const getDialogTitle = () => {
    if (contentType === 'questions' && progress) {
      if (progress.remainingCount === 0 && progress.spanishCount > 0) {
        return 'Re-translate Questions';
      } else if (progress.shouldResume) {
        return 'Resume Translation';
      }
    }
    return 'Translate Course Content';
  };

  const getDialogDescription = () => {
    // Show progress-aware description for questions
    if ((contentType === 'questions' || contentType === 'both') && progress && !isLoadingProgress) {
      if (progress.remainingCount === 0 && progress.spanishCount > 0) {
        return (
          <>
            All {progress.englishCount} questions are already translated to Spanish.
            <br /><br />
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Re-translating will overwrite all existing Spanish translations. This action cannot be undone.
              </AlertDescription>
            </Alert>
          </>
        );
      } else if (progress.shouldResume) {
        return (
          <>
            {progress.spanishCount} questions have already been translated. This will continue translating the remaining {progress.remainingCount} questions.
          </>
        );
      }
    }

    // Default description for modules or when no progress data
    const contentDescription = {
      modules: 'all course modules',
      questions: 'all exam questions',
      both: 'all course modules and exam questions'
    }[contentType];

    return (
      <>
        This will translate {contentDescription} from English to Spanish using AI. 
        {courseId ? ' Only this specific course will be translated.' : ' All courses will be translated.'}
        <br /><br />
        The translation process will:
        <ul className="list-disc list-inside mt-2 space-y-1">
          {(contentType === 'modules' || contentType === 'both') && (
            <>
              <li>Copy the sequence order from English modules</li>
              <li>Skip modules that are already translated</li>
            </>
          )}
          {(contentType === 'questions' || contentType === 'both') && (
            <li>Skip questions that are already translated</li>
          )}
          <li>Retry failed translations automatically</li>
          <li>Preserve all HTML formatting</li>
        </ul>
        <br />
        This operation may take several minutes to complete.
      </>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant={getButtonVariant()} className="gap-2">
          <Languages className="h-4 w-4" />
          {getButtonText()}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>{getDialogTitle()}</DialogTitle>
          <DialogDescription>
            {getDialogDescription()}
          </DialogDescription>
        </DialogHeader>

        {/* Show progress indicator for questions */}
        {(contentType === 'questions' || contentType === 'both') && progress && !isLoadingProgress && (
          <TranslationProgressIndicator
            alreadyTranslated={progress.spanishCount}
            totalQuestions={progress.englishCount}
            isTranslating={translateMutation.isPending}
          />
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setIsOpen(false)}
            disabled={translateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleTranslate}
            disabled={translateMutation.isPending || isLoadingProgress}
            className="gap-2"
          >
            {translateMutation.isPending && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
            {translateMutation.isPending 
              ? 'Translating...' 
              : progress?.shouldResume 
                ? 'Resume Translation' 
                : progress?.remainingCount === 0 && progress?.spanishCount > 0
                  ? 'Re-translate All'
                  : 'Start Translation'
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
